import axios from 'axios';
import { API_URL, API_REFRESH_TOKEN_URL } from './config';

const axiosInstance = axios.create({
    baseURL: API_URL, // Replace with your API base URL
    headers: { 'Content-Type': 'application/json' }
});

/**
 * Funzione chiamata quando anche la refreshToken fallisce (logout forzato + svuotamento store).
 * Viene registrata in App.js con setLogoutHandler per evitare import circolari con lo store.
 */
let logoutHandler = null;

export function setLogoutHandler(handler) {
    logoutHandler = handler;
}

function forceLogout() {
    localStorage.removeItem('token');

    if (logoutHandler) {
        logoutHandler();
    }
}

/**
 * Promise della refreshToken in corso: se più chiamate ricevono 401/403 insieme, la refreshToken viene fatta una sola volta.
 */
let refreshPromise = null;

/**
 * Chiama la refreshToken. Il BE riconosce l'utente dal cookie HttpOnly (withCredentials), quindi non viene passato nessun token.
 * Se va a buon fine salva il nuovo token nel localStorage, altrimenti forza il logout.
 * Usa axios senza interceptor per non mandare il Bearer e non entrare in loop sui 401/403.
 *
 * @returns la response della refreshToken (contiene token e dati dell'utente)
 */
export function requestRefreshToken() {
    if (!refreshPromise) {
        refreshPromise = axios.post(API_REFRESH_TOKEN_URL, null, { withCredentials: true })
            .then(response => {
                localStorage.setItem('token', response.data.token);
                return response;
            })
            .catch(error => {
                forceLogout();
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
}

// Add a request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        // Do something before the request is sent
        const token = localStorage.getItem('token'); // Retrieve auth token from localStorage

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },

    function (error) {
        // Handle the error
        return Promise.reject(error);
    }
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
    function (response) {
        // Do something with the response data
        return response;
    },

    async function (error) {
        const config = error.config;
        const status = error.response && error.response.status;

        // su 401/403 provo una sola volta a rinnovare il token e ripeto la chiamata con il token nuovo
        if ((status === 401 || status === 403) && config && !config._retry && !config.skipAuthRefresh && localStorage.getItem('token')) {
            config._retry = true;

            try {
                await requestRefreshToken();
            } catch (e) {
                // refreshToken fallita: il logout forzato è già stato fatto in requestRefreshToken
                return Promise.reject(error);
            }

            return axiosInstance(config);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
